import { useEffect, useMemo, useRef, useState } from "react";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import { ChevronDown, Search } from "lucide-react";
import { inputCls, inputClsError } from "@/dashboard/ui";

// ISO 3166-1 alpha-2 → regional indicator flag emoji
function flag(cc) {
  return String.fromCodePoint(...[...cc.toUpperCase()].map((ch) => 127397 + ch.charCodeAt(0)));
}

const COUNTRIES = getCountries().map((cc) => ({
  cc,
  name: new Intl.DisplayNames(["en"], { type: "region" }).of(cc),
  dial: `+${getCountryCallingCode(cc)}`,
})).sort((a, b) => a.name.localeCompare(b.name));

const DIAL_INDEX = {};
for (const c of COUNTRIES) DIAL_INDEX[c.dial] = c;

/**
 * International phone input with searchable country selector.
 * value/onChange carry the full international string (e.g. "+44 7700 900123");
 * E.164 normalisation happens server-side. Invalid numbers surface a red inline
 * error through the parent via onValidityChange.
 */
export default function PhoneInput({
  testid, value, onChange, invalid, error, onValidityChange,
  defaultCountry = "GB", label = "Phone", required = false, disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState(defaultCountry);
  const rootRef = useRef(null);

  // Adopt the business-profile country when it arrives after mount (don't override
  // a country the user already picked while typing a number).
  useEffect(() => {
    if (defaultCountry && !value) setCountry(defaultCountry);
  }, [defaultCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whenever the value holds a full international number, the flag/dialling code
  // follows it — including numbers that load asynchronously in edit/reopen flows.
  // Picking a country never changes the value, so this can't fight the user's pick.
  useEffect(() => {
    if (!value || !value.startsWith("+")) return;
    const parsed = parsePhoneNumberFromString(value);
    if (parsed?.country && parsed.country !== country) setCountry(parsed.country);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = useMemo(() => COUNTRIES.find((c) => c.cc === country) || COUNTRIES.find((c) => c.cc === "GB"), [country]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES.slice(0, 60);
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.cc.toLowerCase() === q).slice(0, 60);
  }, [query]);

  const handleNumber = (raw) => {
    let v = raw;
    // Auto-prefix the selected dialling code when the user types a national number —
    // but never duplicate a code that's already there (typed or pasted without "+").
    if (v && !v.startsWith("+") && /^\d/.test(v)) {
      const digits = v.replace(/\D/g, "");
      const dialDigits = active.dial.slice(1); // "+44" → "44"
      if (dialDigits.length > 1 && digits.startsWith(dialDigits) && digits.length > dialDigits.length) {
        // "44 7700…" → "+44 7700…" (normalise once, don't add a second prefix;
        // drop exactly the dial digits and keep the user's own spacing)
        let seen = 0;
        const rest = v.split("").filter((ch) => {
          if (/\d/.test(ch) && seen < dialDigits.length) { seen += 1; return false; }
          return true;
        }).join("").replace(/^[\s()-]+/, "");
        v = `${active.dial} ${rest}`;
      } else {
        v = `${active.dial} ${v.replace(/^0+/, "")}`;
      }
    }
    onChange(v);
    if (onValidityChange) {
      const parsed = parsePhoneNumberFromString(v);
      onValidityChange(!v || !!(parsed && parsed.isValid()));
    }
  };

  const pickCountry = (c) => {
    setCountry(c.cc);   // store the ISO code — the selector derives flag + dial from it
    setOpen(false);
    setQuery("");
    // Re-prefix an existing national number with the new code. Numbers already in
    // international (+…) form are left untouched — they belong to their own country.
    if (value && !value.startsWith("+")) onChange(`${c.dial} ${value.replace(/^0+/, "")}`);
  };

  return (
    <div ref={rootRef}>
      {label && <label htmlFor={`${testid}-number`} className="mb-1.5 block text-xs font-semibold text-neutral-600">{label}{required ? " *" : ""}</label>}
      <div className={`flex overflow-visible rounded-xl border bg-white transition-colors ${invalid ? "border-red-500" : "border-neutral-300 focus-within:border-[#111111]"}`}>
        <button
          type="button"
          data-testid={`${testid}-country`}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className="flex shrink-0 items-center gap-1.5 rounded-l-xl border-r border-neutral-200 px-3 text-sm text-neutral-700 hover:bg-neutral-50"
        >
          <span aria-hidden="true">{flag(active.cc)}</span>
          <span className="font-medium">{active.dial}</span>
          <ChevronDown size={13} className="text-neutral-400" />
        </button>
        <input
          id={`${testid}-number`}
          data-testid={testid}
          type="tel"
          value={value}
          disabled={disabled}
          onChange={(e) => handleNumber(e.target.value)}
          placeholder="7700 900123"
          aria-invalid={invalid || undefined}
          aria-describedby={error ? `${testid}-error` : undefined}
          autoComplete="tel"
          className="w-full rounded-r-xl px-3.5 py-2.5 text-sm text-[#111111] outline-none placeholder:text-neutral-400"
        />
      </div>
      {open && (
        <div className="relative z-30">
          <div className="absolute mt-1.5 w-full max-w-xs rounded-xl border border-neutral-200 bg-white shadow-lg" role="listbox" data-testid={`${testid}-country-list`}>
            <div className="flex items-center gap-2 border-b border-neutral-100 px-3 py-2">
              <Search size={13} className="text-neutral-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code"
                data-testid={`${testid}-country-search`}
                className="w-full py-1 text-sm outline-none"
              />
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {filtered.map((c) => (
                <li key={c.cc}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={c.cc === country}
                    data-testid={`${testid}-country-${c.cc.toLowerCase()}`}
                    onClick={() => pickCountry(c)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-neutral-50 ${c.cc === country ? "font-semibold" : ""}`}
                  >
                    <span aria-hidden="true">{flag(c.cc)}</span>
                    <span className="flex-1">{c.name}</span>
                    <span className="text-neutral-400">{c.dial}</span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && <li className="px-3 py-3 text-xs text-neutral-400">No country matches “{query}”.</li>}
            </ul>
          </div>
        </div>
      )}
      {error && (
        <p id={`${testid}-error`} role="alert" data-testid={`${testid}-error`} className="mt-1.5 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}

/** Standalone validity helper for parent forms. */
export function phoneIsValid(v) {
  if (!v) return false;
  const parsed = parsePhoneNumberFromString(v);
  return !!(parsed && parsed.isValid());
}

export { inputCls, inputClsError };
